'use client';

import { useState, useCallback, useRef } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { CreatePdfJobData } from '@/lib/pdf-queue';

// State machine states
export type BprPreviewState =
  | 'idle'
  | 'validating'
  | 'generating'
  | 'preview'
  | 'submitting'
  | 'success'
  | 'error';

// Generation status for individual items
export type GenerationStatus = 'pending' | 'generating' | 'completed' | 'error' | 'not_required';

export interface SpeakerForPreview {
  id: string;
  full_name: string;
  cv_url: string | null;
}

export interface BprPreviewFormData {
  eventTheme: string;
  eventType: string;
  eventGoal: string;
  eventFormat: string;
  startDate: string;
  endDate: string;
  academicHours: string;
  eventLanguages: string[];
  participantCount: string;
  providerLink: string;
  registrationLink: string;
  onlineUrl?: string;
  city?: string;
  street?: string;
  building?: string;
  organizationalCommittee?: string;
  technicalInstruments?: boolean | null;
}

export interface GenerationProgress {
  speakerCvs: Map<string, GenerationStatus>;
  eventProgram: GenerationStatus;
  educationalProgram: GenerationStatus;
}

export interface GeneratedUrls {
  speakerCvs: Map<string, string>;
  eventProgramUrl: string | null;
  educationalProgramUrl: string | null;
}

export interface BprPreviewError {
  message: string;
  failedItem?: string;
  canRetry: boolean;
  /** Machine-readable code from the API, e.g. PASSWORD_REQUIRED. */
  code?: string;
}

interface UseBprPreviewOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  pollingInterval?: number;
  maxPollingAttempts?: number;
}

interface JobStatusResponse {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  pdf_url?: string;
  error_message?: string;
}

// Concurrency limit for parallel PDF generation
const MAX_CONCURRENT_GENERATIONS = 3;

// Simple hash function for content comparison
// Uses djb2 algorithm - fast and good distribution for strings
function hashContent(content: string): string {
  let hash = 5381;
  for (let i = 0; i < content.length; i++) {
    hash = ((hash << 5) + hash) ^ content.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer and then to hex string
  return (hash >>> 0).toString(16);
}

// Check if content has changed since last generation
async function hasContentChanged(
  eventId: string,
  type: 'program' | 'educational_program' | 'cv',
  contentHash: string,
  speakerId?: string
): Promise<{ changed: boolean; existingUrl: string | null }> {
  try {
    // Build query to find the last successful job for this content
    let query = `/api/pdf-jobs/check-hash?event_id=${eventId}&type=${type}&content_hash=${contentHash}`;
    if (speakerId) {
      query += `&speaker_id=${speakerId}`;
    }

    const response = await fetch(query);
    if (!response.ok) {
      // If check fails, assume content changed to be safe
      return { changed: true, existingUrl: null };
    }

    const data = await response.json();
    return {
      changed: !data.match,
      existingUrl: data.match ? data.pdf_url : null,
    };
  } catch {
    // On error, assume content changed to be safe
    return { changed: true, existingUrl: null };
  }
}

export function useBprPreview(options: UseBprPreviewOptions = {}) {
  const {
    onSuccess,
    onError,
    pollingInterval = 2000,
    maxPollingAttempts = 60,
  } = options;

  const { t } = useLanguage();

  // State
  const [state, setState] = useState<BprPreviewState>('idle');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress>({
    speakerCvs: new Map(),
    eventProgram: 'pending',
    educationalProgram: 'not_required',
  });
  const [generatedUrls, setGeneratedUrls] = useState<GeneratedUrls>({
    speakerCvs: new Map(),
    eventProgramUrl: null,
    educationalProgramUrl: null,
  });
  const [errorDetails, setErrorDetails] = useState<BprPreviewError | null>(null);

  // Abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);


  // Poll for job status
  const pollJobStatus = useCallback(
    async (jobId: string, signal: AbortSignal): Promise<string> => {
      let attempt = 0;

      while (attempt < maxPollingAttempts) {
        if (signal.aborted) {
          throw new Error('Generation cancelled');
        }

        const response = await fetch(`/api/pdf-jobs/${jobId}`, { signal });

        if (!response.ok) {
          throw new Error(t('mozSubmission.errors.failedToCheckJobStatus'));
        }

        const data: JobStatusResponse = await response.json();

        switch (data.status) {
          case 'completed':
            if (!data.pdf_url) {
              throw new Error('Job completed but no PDF URL returned');
            }
            return data.pdf_url;

          case 'failed':
            throw new Error(data.error_message || 'PDF generation failed');

          case 'processing':
          case 'pending':
          default:
            await new Promise((resolve) => setTimeout(resolve, pollingInterval));
            attempt++;
        }
      }

      throw new Error('PDF generation timeout');
    },
    [pollingInterval, maxPollingAttempts, t]
  );

  // Generate a single PDF
  const generateSinglePdf = useCallback(
    async (jobData: CreatePdfJobData, signal: AbortSignal): Promise<string> => {
      const createResponse = await fetch('/api/pdf-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData),
        signal,
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.error || t('mozSubmission.errors.failedToCreatePdfJob'));
      }

      const { job_id } = await createResponse.json();
      return pollJobStatus(job_id, signal);
    },
    [pollJobStatus, t]
  );

  // Generate speaker CV
  const generateSpeakerCv = useCallback(
    async (speaker: SpeakerForPreview, signal: AbortSignal, eventId?: string): Promise<string> => {
      // Update status to generating
      setGenerationProgress((prev) => ({
        ...prev,
        speakerCvs: new Map(prev.speakerCvs).set(speaker.id, 'generating'),
      }));

      try {
        // Fetch full speaker data for CV generation
        let speakerDataHash: string | undefined;
        let speakerDetails: {
          first_name?: string;
          last_name?: string;
          middle_name?: string;
          full_name?: string;
          bio?: string;
          date_of_birth?: string;
          email?: string;
          phone?: string;
          education?: string;
          workplace?: string;
          position?: string;
          academic_degree?: string;
          academic_title?: string;
          photo_url?: string;
          additional_info?: string;
        } = {};

        try {
          const hashResponse = await fetch(
            `/api/pdf-jobs/check-speaker-change?speaker_id=${speaker.id}${eventId ? `&event_id=${eventId}` : ''}`
          );
          if (hashResponse.ok) {
            const hashData = await hashResponse.json();
            speakerDataHash = hashData.currentHash;
            // The check-speaker-change endpoint returns speaker data, extract it
            if (hashData.speakerData) {
              speakerDetails = hashData.speakerData;
            }
          }
        } catch {
          // Continue without hash if fetch fails
        }

        // If we didn't get speaker details from hash check, fetch them directly
        if (!speakerDetails.first_name) {
          try {
            const speakerResponse = await fetch(`/api/speakers/${speaker.id}`);
            if (speakerResponse.ok) {
              speakerDetails = await speakerResponse.json();
            }
          } catch {
            // Continue with minimal data if fetch fails
          }
        }

        const pdfUrl = await generateSinglePdf(
          {
            type: 'cv',
            metadata: {
              speaker_id: speaker.id,
              event_id: eventId, // Required for PDF filename generation
              speaker_data_hash: speakerDataHash, // Store hash for future comparisons
              // Speaker details for CV content
              speaker_name: speakerDetails.full_name || speaker.full_name || 'Unknown Speaker',
              date_of_birth: speakerDetails.date_of_birth || '',
              email: speakerDetails.email || '',
              phone: speakerDetails.phone || '',
              education: speakerDetails.education || '',
              workplace: speakerDetails.workplace || '',
              position: speakerDetails.position || '',
              academic_degree: speakerDetails.academic_degree || '',
              academic_title: speakerDetails.academic_title || '',
              photo_url: speakerDetails.photo_url || '',
              additional_info: speakerDetails.additional_info || speakerDetails.bio || '',
            },
          },
          signal
        );

        // Update status to completed
        setGenerationProgress((prev) => ({
          ...prev,
          speakerCvs: new Map(prev.speakerCvs).set(speaker.id, 'completed'),
        }));

        setGeneratedUrls((prev) => ({
          ...prev,
          speakerCvs: new Map(prev.speakerCvs).set(speaker.id, pdfUrl),
        }));

        return pdfUrl;
      } catch (error) {
        setGenerationProgress((prev) => ({
          ...prev,
          speakerCvs: new Map(prev.speakerCvs).set(speaker.id, 'error'),
        }));
        throw error;
      }
    },
    [generateSinglePdf]
  );

  // Generate CVs with concurrency limit
  const generateSpeakerCvsParallel = useCallback(
    async (speakers: SpeakerForPreview[], signal: AbortSignal, eventId?: string): Promise<Map<string, string>> => {
      const results = new Map<string, string>();
      const queue = [...speakers];
      const executing: Promise<void>[] = [];
      const errors: string[] = [];

      const processNext = async (): Promise<void> => {
        if (queue.length === 0) return;

        const speaker = queue.shift()!;
        try {
          const url = await generateSpeakerCv(speaker, signal, eventId);
          results.set(speaker.id, url);
        } catch {
          errors.push(speaker.full_name);
        }
      };

      // Start initial batch
      while (queue.length > 0 && executing.length < MAX_CONCURRENT_GENERATIONS) {
        const promise = processNext();
        executing.push(promise);
      }

      // Wait for all to complete
      await Promise.all(executing);

      // Process remaining in batches
      while (queue.length > 0) {
        const batch = [];
        while (queue.length > 0 && batch.length < MAX_CONCURRENT_GENERATIONS) {
          batch.push(processNext());
        }
        await Promise.all(batch);
      }

      if (errors.length > 0 && errors.length === speakers.length) {
        throw new Error(`Failed to generate CVs for: ${errors.join(', ')}`);
      }

      return results;
    },
    [generateSpeakerCv]
  );

  // Main generation function
  const startGeneration = useCallback(
    async (
      speakers: SpeakerForPreview[],
      generateProgramHtml: () => string | null,
      generateEducationalProgramHtml: (() => string | null) | null,
      requiresEducationalProgram: boolean,
      existingUrls: {
        eventProgramUrl: string | null;
        educationalProgramUrl: string | null;
      },
      eventMetadata: {
        eventId: string;
        userId: string;
        eventTitle: string;
      }
    ) => {
      // Create abort controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Reset state
      setState('generating');
      setProgress(0);
      setErrorDetails(null);

      // Generate HTML content first to check if it has changed
      setCurrentStep(t('bprPreview.checkingChanges') || 'Checking for changes...');

      const programHtml = generateProgramHtml();
      const educationalProgramHtml = generateEducationalProgramHtml?.() || null;

      // Calculate content hashes
      const programHash = programHtml ? hashContent(programHtml) : null;
      const educationalHash = educationalProgramHtml ? hashContent(educationalProgramHtml) : null;

      // Check which PDFs need regeneration based on content changes
      let needsProgramPdf = !!programHtml;
      let needsEducationalPdf = requiresEducationalProgram && !!educationalProgramHtml;
      let cachedProgramUrl: string | null = null;
      let cachedEducationalUrl: string | null = null;

      // Check if program content has changed
      if (programHash) {
        const programCheck = await hasContentChanged(
          eventMetadata.eventId,
          'program',
          programHash
        );
        if (!programCheck.changed && programCheck.existingUrl) {
          needsProgramPdf = false;
          cachedProgramUrl = programCheck.existingUrl;
        }
      }

      // Check if educational program content has changed
      if (educationalHash && requiresEducationalProgram) {
        const eduCheck = await hasContentChanged(
          eventMetadata.eventId,
          'educational_program',
          educationalHash
        );
        if (!eduCheck.changed && eduCheck.existingUrl) {
          needsEducationalPdf = false;
          cachedEducationalUrl = eduCheck.existingUrl;
        }
      }

      // Check which speakers need CV regeneration
      const speakersNeedingCv: SpeakerForPreview[] = [];
      const cachedSpeakerCvs = new Map<string, string>();
      const speakerDataHashes = new Map<string, string>();

      // Check each speaker for changes
      // Always check pdf_jobs for existing CV, regardless of event_speakers.cv_url
      // This prevents unnecessary regeneration when user goes back without submitting
      for (const speaker of speakers) {
        try {
          const response = await fetch(
            `/api/pdf-jobs/check-speaker-change?speaker_id=${speaker.id}&event_id=${eventMetadata.eventId}`
          );
          if (response.ok) {
            const data = await response.json();
            if (data.needsRegeneration) {
              speakersNeedingCv.push(speaker);
              if (data.currentHash) {
                speakerDataHashes.set(speaker.id, data.currentHash);
              }
            } else if (data.existingUrl) {
              cachedSpeakerCvs.set(speaker.id, data.existingUrl);
            } else {
              // No existing URL and no regeneration needed - shouldn't happen, but handle gracefully
              speakersNeedingCv.push(speaker);
            }
          } else {
            // On error, regenerate to be safe
            speakersNeedingCv.push(speaker);
          }
        } catch {
          // On error, regenerate to be safe
          speakersNeedingCv.push(speaker);
        }
      }

      const initialProgress: GenerationProgress = {
        speakerCvs: new Map(
          speakers.map((s) => [
            s.id,
            speakersNeedingCv.some((sn) => sn.id === s.id) ? 'pending' : 'completed',
          ])
        ),
        eventProgram: needsProgramPdf ? 'pending' : 'completed',
        educationalProgram: requiresEducationalProgram
          ? (needsEducationalPdf ? 'pending' : 'completed')
          : 'not_required',
      };
      setGenerationProgress(initialProgress);

      // Initialize URLs with cached ones
      setGeneratedUrls({
        speakerCvs: cachedSpeakerCvs,
        eventProgramUrl: cachedProgramUrl,
        educationalProgramUrl: cachedEducationalUrl,
      });

      // Calculate total steps
      const totalSteps =
        speakersNeedingCv.length +
        (needsProgramPdf ? 1 : 0) +
        (needsEducationalPdf ? 1 : 0);

      if (totalSteps === 0) {
        // Nothing to generate, go straight to preview
        setProgress(100);
        setState('preview');
        return;
      }

      let completedSteps = 0;

      try {
        // Step 1: Generate program PDFs first (in parallel)
        const pdfPromises: Promise<void>[] = [];

        if (needsProgramPdf && programHtml) {
          setCurrentStep(t('bprPreview.generatingProgram') || 'Generating event program...');
          setGenerationProgress((prev) => ({
            ...prev,
            eventProgram: 'generating',
          }));

          const programPromise = (async () => {
            const pdfUrl = await generateSinglePdf(
              {
                type: 'program',
                html_content: programHtml,
                metadata: {
                  event_id: eventMetadata.eventId,
                  user_id: eventMetadata.userId,
                  event_title: eventMetadata.eventTitle,
                  old_program_url: existingUrls.eventProgramUrl,
                  content_hash: programHash, // Store hash for future comparisons
                },
              },
              signal
            );

            setGenerationProgress((prev) => ({
              ...prev,
              eventProgram: 'completed',
            }));

            setGeneratedUrls((prev) => ({
              ...prev,
              eventProgramUrl: pdfUrl,
            }));

            completedSteps++;
            setProgress((completedSteps / totalSteps) * 100);
          })();

          pdfPromises.push(programPromise);
        }

        if (needsEducationalPdf && educationalProgramHtml) {
          setCurrentStep(t('bprPreview.generatingEducational') || 'Generating educational program...');
          setGenerationProgress((prev) => ({
            ...prev,
            educationalProgram: 'generating',
          }));

          const eduPromise = (async () => {
            const pdfUrl = await generateSinglePdf(
              {
                type: 'educational_program',
                html_content: educationalProgramHtml,
                metadata: {
                  event_id: eventMetadata.eventId,
                  user_id: eventMetadata.userId,
                  event_title: `${eventMetadata.eventTitle} - Навчальна програма`,
                  old_program_url: existingUrls.educationalProgramUrl,
                  content_hash: educationalHash, // Store hash for future comparisons
                },
              },
              signal
            );

            setGenerationProgress((prev) => ({
              ...prev,
              educationalProgram: 'completed',
            }));

            setGeneratedUrls((prev) => ({
              ...prev,
              educationalProgramUrl: pdfUrl,
            }));

            completedSteps++;
            setProgress((completedSteps / totalSteps) * 100);
          })();

          pdfPromises.push(eduPromise);
        }

        await Promise.all(pdfPromises);

        // Step 2: Generate speaker CVs last
        if (speakersNeedingCv.length > 0) {
          setCurrentStep(t('bprPreview.generatingCvs') || 'Generating speaker CVs...');
          const cvResults = await generateSpeakerCvsParallel(speakersNeedingCv, signal, eventMetadata.eventId);

          completedSteps += speakersNeedingCv.length;
          setProgress((completedSteps / totalSteps) * 100);

          // Merge new CV URLs
          setGeneratedUrls((prev) => ({
            ...prev,
            speakerCvs: new Map([...prev.speakerCvs, ...cvResults]),
          }));
        }

        // Success - go to preview
        setProgress(100);
        setCurrentStep('');
        setState('preview');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setErrorDetails({
          message: errorMessage,
          canRetry: true,
        });
        setState('error');
        onError?.(errorMessage);
      }
    },
    [generateSpeakerCvsParallel, generateSinglePdf, t, onError]
  );

  // Cancel generation
  const cancelGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    setState('idle');
    setProgress(0);
    setCurrentStep('');
    setErrorDetails(null);
  }, []);

  // Start submission to MOZ
  // password is optional: when omitted the server decrypts the organizer's
  // stored БПР password instead of the browser sending one.
  const startSubmission = useCallback(async (eventId: string, credentials: { email: string; password?: string }) => {
    setState('submitting');
    setCurrentStep(t('bprPreview.submitting') || 'Submitting to MOZ...');

    try {
      // First, save the generated URLs to the database
      const urlsToSave: {
        event_program_url?: string;
        educational_program_url?: string;
        speaker_cvs?: { speaker_id: string; cv_url: string }[];
      } = {};

      if (generatedUrls.eventProgramUrl) {
        urlsToSave.event_program_url = generatedUrls.eventProgramUrl;
      }

      if (generatedUrls.educationalProgramUrl) {
        urlsToSave.educational_program_url = generatedUrls.educationalProgramUrl;
      }

      // Collect speaker CV URLs
      if (generatedUrls.speakerCvs.size > 0) {
        urlsToSave.speaker_cvs = Array.from(generatedUrls.speakerCvs.entries()).map(
          ([speakerId, cvUrl]) => ({ speaker_id: speakerId, cv_url: cvUrl })
        );
      }

      // Save generated URLs to database before submission
      const saveResponse = await fetch('/api/moz-submission/save-urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          ...urlsToSave,
        }),
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save generated documents');
      }

      // Now proceed with MOZ submission
      const response = await fetch('/api/moz-submission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          credentials,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle known error codes with translations
        let errorMessage: string;
        if (errorData.error === 'BPR_SUBMISSION_FAILED') {
          errorMessage = t('mozSubmission.statusMessages.failedToSubmit');
          if (errorData.details) {
            errorMessage += ` ${t('mozSubmission.statusMessages.details')} ${errorData.details}`;
          }
        } else if (errorData.error === 'MISSING_LOCATION_FIELDS') {
          errorMessage = t('mozSubmission.errors.missingLocationFields') || 'Missing required location fields. Please fill in all address fields for in-person/hybrid events.';
          if (errorData.details) {
            errorMessage += ` ${errorData.details}`;
          }
        } else if (errorData.error === 'PASSWORD_REQUIRED') {
          errorMessage = t('mozSubmission.errors.passwordRequired')
            || 'Збережений пароль недоступний. Введіть пароль до кабінету БПР вручну.';
        } else if (errorData.error === 'NOT_ORGANIZER_OWNER') {
          errorMessage = t('mozSubmission.errors.notOrganizerOwner')
            || 'Лише власник профілю організатора може подавати заявку зі збереженим паролем. Введіть пароль вручну.';
        } else {
          errorMessage = errorData.error || t('mozSubmission.statusMessages.failedToSubmit');
        }
        const submissionError = new Error(errorMessage) as Error & { code?: string };
        if (typeof errorData.error === 'string') submissionError.code = errorData.error;
        throw submissionError;
      }

      setState('success');
      setCurrentStep('');
      onSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrorDetails({
        message: errorMessage,
        canRetry: true,
        code: (error as { code?: string })?.code,
      });
      setState('error');
      onError?.(errorMessage);
    }
  }, [t, onSuccess, onError, generatedUrls]);

  // Reset to idle
  const reset = useCallback(() => {
    setState('idle');
    setProgress(0);
    setCurrentStep('');
    setValidationErrors([]);
    setGenerationProgress({
      speakerCvs: new Map(),
      eventProgram: 'pending',
      educationalProgram: 'not_required',
    });
    setGeneratedUrls({
      speakerCvs: new Map(),
      eventProgramUrl: null,
      educationalProgramUrl: null,
    });
    setErrorDetails(null);
  }, []);

  // Open modal (transition to validating then generating)
  const openPreview = useCallback(() => {
    setState('validating');
  }, []);

  // Set validation result
  const setValidationResult = useCallback((errors: string[]) => {
    setValidationErrors(errors);
    if (errors.length > 0) {
      setState('error');
      setErrorDetails({
        message: errors[0],
        canRetry: false,
      });
    }
  }, []);

  return {
    // State
    state,
    progress,
    currentStep,
    validationErrors,
    generationProgress,
    generatedUrls,
    errorDetails,

    // Actions
    openPreview,
    setValidationResult,
    startGeneration,
    cancelGeneration,
    startSubmission,
    reset,

    // Derived state
    isOpen: state !== 'idle',
    isGenerating: state === 'generating',
    isPreviewReady: state === 'preview',
    isSubmitting: state === 'submitting',
    hasError: state === 'error',
  };
}
